// src/lib/onboarding/onboardingService.js
// All DB reads and writes for onboarding state.
// Never reads from or writes to localStorage.
// Every function is a no-op (returns null/void) when Supabase is unavailable.

import { supabase, hasSupabase } from "../supabaseClient";

const TABLE = "onboarding_states";

// ─────────────────────────────────────────────────────────────────────────────
// getOnboardingState
// Returns the current onboarding state for a user, or null if not found.
// Used by the guard (App.jsx) and the useOnboardingState hook.
// ─────────────────────────────────────────────────────────────────────────────
export async function getOnboardingState(userId) {
  if (!hasSupabase || !userId) return null;

  const { data, error } = await supabase
    .from(TABLE)
    .select("*")
    .eq("user_id", userId)
    .single();

  if (error) {
    // PGRST116 = row not found (user pre-dates migration 004)
    if (error.code === "PGRST116") return null;
    // Throw real errors (network, RLS, timeout) so callers can retry.
    throw new Error(`getOnboardingState: ${error.message}`);
  }

  return data;
}

// ─────────────────────────────────────────────────────────────────────────────
// createOnboardingState
// Called for users who pre-date migration 004 and have no row yet.
// The DB trigger handles all new signups automatically — this is only
// a safety net for existing accounts.
// ─────────────────────────────────────────────────────────────────────────────
export async function createOnboardingState(userId) {
  if (!hasSupabase || !userId) return;

  const { error } = await supabase.from(TABLE).upsert(
    { user_id: userId, status: "pending", current_step: 0, steps_completed: [], completed_at: null },
    { onConflict: "user_id", ignoreDuplicates: true }
  );

  if (error) {
    console.warn("[onboardingService] createOnboardingState error:", error.message);
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// ensureOnboardingState
// Called on every login via useAuth.js, mirroring the existing ensureProfile.
//
// Decision tree:
//   Row exists           → do nothing (new signup: trigger already set pending;
//                          returning user: leave current status intact)
//   Row missing + new    → DB trigger already handled it (won't reach here)
//   Row missing + legacy → user pre-dates migration 004; create as "complete"
//                          so they are never redirected to onboarding
//
// Never throws — on any error we log and move on so auth is never blocked.
// ─────────────────────────────────────────────────────────────────────────────
export async function ensureOnboardingState(userId) {
  if (!hasSupabase || !userId) return;

  const existing = await getOnboardingState(userId);
  if (existing) return; // row already present — nothing to do

  // No row found → legacy account pre-dating migration 004.
  // Mark complete so they skip onboarding on next render.
  const { error } = await supabase.from(TABLE).upsert(
    {
      user_id:         userId,
      status:          "complete",
      current_step:    0,
      steps_completed: [],
      completed_at:    new Date().toISOString(),
    },
    { onConflict: "user_id", ignoreDuplicates: true }
  );

  if (error) {
    console.warn("[onboardingService] ensureOnboardingState error:", error.message);
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// advanceOnboardingStep
// Called after each step is completed. Marks status as in_progress,
// increments current_step, and appends the completed step ID.
// ─────────────────────────────────────────────────────────────────────────────
export async function advanceOnboardingStep(userId, completedStep) {
  if (!hasSupabase || !userId) return;

  const state = await getOnboardingState(userId);
  if (!state) {
    console.warn("[onboardingService] advanceOnboardingStep: no state found for", userId);
    return;
  }

  const alreadyCompleted = Array.isArray(state.steps_completed)
    ? state.steps_completed
    : [];

  const { error } = await supabase
    .from(TABLE)
    .update({
      status:          "in_progress",
      current_step:    state.current_step + 1,
      steps_completed: [...alreadyCompleted, completedStep],
    })
    .eq("user_id", userId);

  if (error) {
    console.warn("[onboardingService] advanceOnboardingStep error:", error.message);
    throw error; // re-throw so the UI can show the error state
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// completeOnboarding
// Called ONLY when the final step is reached.
// Sets status = "complete" and records the completion timestamp.
// ─────────────────────────────────────────────────────────────────────────────
export async function completeOnboarding(userId) {
  if (!hasSupabase || !userId) return;

  const { error } = await supabase
    .from(TABLE)
    .update({
      status:       "complete",
      completed_at: new Date().toISOString(),
    })
    .eq("user_id", userId);

  if (error) {
    console.warn("[onboardingService] completeOnboarding error:", error.message);
    throw error; // re-throw so the UI can show the error state
  }
}
